/* Cockpit grid layout — single source of truth helpers.

   The working layout lives in the `useChat` zustand store (store.ts) so the
   LLM dashboard actions (add_panel / move_panel / remove_panel) and the
   user's drag/resize edits mutate the SAME layout the grid renders.
   This module owns the defaults, localStorage persistence, and the merge
   logic that gives LLM-created dynamic panels a placement. */

import type { Layout, LayoutItem } from "react-grid-layout/legacy"

export const LAYOUT_STORAGE_KEY = "supplylens-cockpit-layout-v1"

// Default layout: 12-col grid, rowHeight=60. Mirrors the previous CSS-grid arrangement.
const LEGACY_DEFAULT_LAYOUT: Layout = [
  { i: "streams", x: 0, y: 0, w: 8, h: 6 },
  { i: "fleet", x: 8, y: 0, w: 4, h: 6 },
  { i: "lanes", x: 0, y: 6, w: 4, h: 9 },
  { i: "risk", x: 4, y: 6, w: 4, h: 3 },
  { i: "intel", x: 8, y: 6, w: 4, h: 9 },
  { i: "market", x: 4, y: 9, w: 4, h: 6 },
  { i: "infra", x: 0, y: 15, w: 4, h: 6 },
  { i: "signals", x: 4, y: 15, w: 4, h: 6 },
  { i: "escalation", x: 8, y: 15, w: 4, h: 6 },
  { i: "ai-forecasts", x: 0, y: 21, w: 4, h: 6 },
  { i: "live-intel", x: 4, y: 21, w: 4, h: 6 },
  { i: "trade-counter", x: 8, y: 21, w: 4, h: 3 },
  { i: "ai-market", x: 8, y: 24, w: 4, h: 5 },
  { i: "force-posture", x: 0, y: 27, w: 6, h: 4 },
  { i: "port-conditions", x: 6, y: 27, w: 6, h: 6 },
  { i: "predictions", x: 0, y: 33, w: 6, h: 8 },
  { i: "economic", x: 6, y: 33, w: 6, h: 3 },
  { i: "warehouse", x: 6, y: 36, w: 6, h: 8 },
  { i: "globe", x: 0, y: 41, w: 12, h: 15 },
  { i: "chat", x: 6, y: 56, w: 6, h: 10 },
]

// Put the global picture and actionable signals first; retain every original panel.
export const DEFAULT_LAYOUT: Layout = [
  { i: "globe", x: 0, y: 0, w: 8, h: 7 },
  { i: "intel", x: 8, y: 0, w: 4, h: 7 },
  { i: "market", x: 0, y: 9, w: 6, h: 6 },
  { i: "lanes", x: 6, y: 9, w: 6, h: 6 },
  { i: "fleet", x: 0, y: 15, w: 8, h: 6 },
  { i: "risk", x: 8, y: 15, w: 4, h: 6 },
  { i: "port-conditions", x: 0, y: 21, w: 6, h: 7 },
  { i: "warehouse", x: 6, y: 21, w: 6, h: 7 },
  { i: "signals", x: 0, y: 28, w: 4, h: 6 },
  { i: "escalation", x: 4, y: 28, w: 4, h: 6 },
  { i: "infra", x: 8, y: 28, w: 4, h: 6 },
  { i: "ai-forecasts", x: 0, y: 34, w: 6, h: 6 },
  { i: "live-intel", x: 6, y: 34, w: 6, h: 6 },
  { i: "trade-counter", x: 0, y: 40, w: 6, h: 4 },
  { i: "ai-market", x: 6, y: 40, w: 6, h: 4 },
  { i: "force-posture", x: 0, y: 44, w: 6, h: 5 },
  { i: "economic", x: 6, y: 44, w: 6, h: 5 },
  { i: "predictions", x: 0, y: 49, w: 6, h: 8 },
  { i: "chat", x: 6, y: 49, w: 6, h: 8 },
  { i: "streams", x: 0, y: 57, w: 12, h: 6 },
]

/** Reflow for smaller screens without overwriting the saved desktop layout. */
export function responsiveLayout(layout: Layout, cols: 1 | 6): Layout {
  let x = 0
  let y = 0
  let rowHeight = 0
  return [...layout].sort((a, b) => a.y - b.y || a.x - b.x).map((item) => {
    const w = cols === 1 ? 1 : item.w >= 8 ? 6 : 3
    if (x + w > cols) { x = 0; y += rowHeight; rowHeight = 0 }
    const h = item.i === "globe" ? 8 : Math.max(4, Math.min(item.h, 9))
    const next = { ...item, x, y, w, h, minW: 1, maxW: cols }
    x += w
    rowHeight = Math.max(rowHeight, h)
    return next
  })
}

function isLayoutItem(value: unknown): value is LayoutItem {
  if (typeof value !== "object" || value === null) return false
  const v = value as Record<string, unknown>
  return (
    typeof v.i === "string" &&
    typeof v.x === "number" &&
    typeof v.y === "number" &&
    typeof v.w === "number" &&
    typeof v.h === "number"
  )
}

/**
 * Validate a parsed candidate layout.
 *
 * Accepts layouts whose panel-id set overlaps the default schema (so panels
 * removed via chat stay removed across reloads), but rejects layouts that
 * share NO ids with the defaults — that means the panel-id schema drifted
 * across releases and the stored layout is garbage for this build.
 */
export function validateStoredLayout(parsed: unknown): Layout | null {
  if (!Array.isArray(parsed) || parsed.length === 0) return null
  if (!parsed.every(isLayoutItem)) return null
  const defaultIds = new Set(DEFAULT_LAYOUT.map((l) => l.i))
  const overlap = parsed.some((l) => defaultIds.has(l.i))
  return overlap ? (parsed as Layout) : null
}

export function readStoredLayout(): Layout | null {
  if (typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(LAYOUT_STORAGE_KEY)
    if (!raw) return null
    const stored = validateStoredLayout(JSON.parse(raw) as unknown)
    // Migrate only the untouched old default. Preserve customized and saved layouts.
    const isOldDefault = stored?.length === LEGACY_DEFAULT_LAYOUT.length && LEGACY_DEFAULT_LAYOUT.every(old => {
      const item = stored.find(item => item.i === old.i)
      return item && item.x === old.x && item.y === old.y && item.w === old.w && item.h === old.h
    })
    return isOldDefault ? DEFAULT_LAYOUT : stored
  } catch {
    return null
  }
}

export function persistLayout(layout: Layout): void {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layout))
  } catch {
    // localStorage may be unavailable (private mode, disk full) — ignore silently.
  }
}

export function clearStoredLayout(): void {
  if (typeof window === "undefined") return
  try {
    window.localStorage.removeItem(LAYOUT_STORAGE_KEY)
  } catch {
    // ignore
  }
}

/**
 * Ensure every dynamic-panel id has a placement in the layout. Extras are
 * appended far below existing content; react-grid-layout's vertical
 * compaction pulls them up on first render.
 */
export function mergeLayoutWithDynamic(layout: Layout, dynamicIds: string[]): Layout {
  const existing = new Set(layout.map((l) => l.i))
  const extras: LayoutItem[] = []
  let y = 999
  for (const id of dynamicIds) {
    if (!existing.has(id)) {
      extras.push({ i: id, x: 0, y, w: 6, h: 6 })
      y += 6
    }
  }
  return extras.length === 0 ? layout : [...layout, ...extras]
}
